import { pushToast } from '@shared/ui/kit'
import { canAccessAdminPath } from '~/shared/config'
import { useAdminSessionManager } from '~/features/auth'
import {
  SUPPORT_WS_EVENTS,
  useSupportSocket,
  useSupportSound,
  type AdminSupportTicket,
  type SupportMessage,
} from '~/features/support-chat/@x/admin'

/**
 * Глобальные оповещения чата поддержки для админки (admin-kit): звук + тост
 * о новом обращении и сообщении клиента с ЛЮБОЙ страницы админки — сапорт
 * слышит тикет, даже если сидит в заказах или аналитике.
 *
 * На самой странице чата (/administration/support) молчит: там свои
 * обработчики с тем же звуком и контекстными тостами — иначе звучало бы дважды.
 *
 * Вызывается из layout admin-kit. Подключение идемпотентно: сокет — синглтон,
 * страница чата переиспользует это же соединение.
 */
export function useAdminSupportAlerts(): void {
  const route = useRoute()
  const adminSession = useAdminSessionManager()
  const socket = useSupportSocket()
  const { playTicket, playMessage } = useSupportSound()

  let unsubscribers: Array<() => void> = []

  /** Страница чата (без /stickers и других подстраниц) — там свои обработчики */
  const isOnSupportChatPage = (): boolean => /\/administration\/support\/?$/.test(route.path)

  function clientName(ticket: AdminSupportTicket): string {
    const name = [ticket.user?.firstName, ticket.user?.lastName].filter(Boolean).join(' ')
    return name || ticket.user?.phone || 'Без имени'
  }

  onMounted(() => {
    // Гейт по роли: manager не имеет доступа к чату поддержки — ему не звоним
    if (!canAccessAdminPath(adminSession.getAdminRole(), '/administration/support')) return
    if (!socket.connect('admin')) return

    unsubscribers = [
      socket.on<{ ticket: AdminSupportTicket }>(SUPPORT_WS_EVENTS.TICKET_NEW, (payload) => {
        if (isOnSupportChatPage()) return
        playTicket()
        pushToast({
          title: 'Новое обращение в поддержку',
          description: clientName(payload.ticket),
          color: payload.ticket.type === 'payment' ? 'danger' : 'default',
        })
      }),
      socket.on<{ message: SupportMessage, ticket?: AdminSupportTicket }>(
        SUPPORT_WS_EVENTS.MESSAGE_NEW,
        (payload) => {
          if (isOnSupportChatPage()) return
          if (payload.message.authorType !== 'user') return
          playMessage()
          if (payload.ticket) {
            pushToast({
              title: 'Сообщение в поддержку',
              description: clientName(payload.ticket),
              color: payload.ticket.type === 'payment' ? 'danger' : 'default',
            })
          }
        },
      ),
    ]
  })

  onUnmounted(() => {
    for (const unsubscribe of unsubscribers) unsubscribe()
    unsubscribers = []
  })
}
